import { useCallback, useEffect, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { CLIENTS_PAGE_SIZE } from "../constants/clients.constants";
import { buildClientsQueryParams, type InternalClientsListParams } from "../services/clientsService";
import type { ClientsFiltersState } from "../types/clients.types";

export const getDefaultClientsFilters = (): ClientsFiltersState => ({
  client_type: "",
  kyc_status: "",
  country: "",
  search: "",
});

export interface PaginatedClientsQuery<TRow> {
  filters: ClientsFiltersState;
  setFilters: React.Dispatch<React.SetStateAction<ClientsFiltersState>>;
  /** Filters currently applied to the query (snapshot taken on `applyFilters`). */
  appliedFilters: ClientsFiltersState;
  page: number;
  pageSize: number;
  rows: TRow[];
  total: number;
  isLoading: boolean;
  error: string | null;
  noAccess: boolean;
  /** True when the empty result comes from an applied filter/search rather than an empty dataset. */
  hasActiveFilters: boolean;
  applyFilters: () => void;
  resetFilters: () => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  refetch: () => void;
  /** Removes a single applied filter (used by the removable filter chips). */
  removeFilter: (key: keyof ClientsFiltersState) => void;
}

/**
 * Shared pagination + apply-on-demand filter state for the clients master-detail tables. The `fetcher`
 * receives the built query params and returns the page rows plus the server total — both the organizations
 * portfolio (tree endpoint) and the clients list reuse this exact lifecycle.
 */
export function usePaginatedClientsQuery<TRow>(
  fetcher: (params: InternalClientsListParams) => Promise<{ rows: TRow[]; total: number }>,
): PaginatedClientsQuery<TRow> {
  const [filters, setFilters] = useState<ClientsFiltersState>(getDefaultClientsFilters);
  const [appliedFilters, setAppliedFilters] = useState<ClientsFiltersState>(getDefaultClientsFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(CLIENTS_PAGE_SIZE);
  const [rows, setRows] = useState<TRow[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noAccess, setNoAccess] = useState(false);

  const fetchPage = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setNoAccess(false);
    try {
      const params = buildClientsQueryParams(appliedFilters, page, pageSize);
      const result = await fetcher(params);
      setRows(result.rows);
      setTotal(result.total);
    } catch (err) {
      if (HttpApiService.isAccessDeniedError(err)) {
        setNoAccess(true);
      } else {
        setError(HttpApiService.getErrorMessage(err));
      }
      setRows([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [appliedFilters, page, pageSize, fetcher]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const hasActiveFilters = Object.values(appliedFilters).some((value) => value.trim() !== "");

  const applyFilters = useCallback(() => {
    setAppliedFilters(filters);
    setPage(1);
  }, [filters]);

  const resetFilters = useCallback(() => {
    const empty = getDefaultClientsFilters();
    setFilters(empty);
    setAppliedFilters(empty);
    setPage(1);
  }, []);

  const removeFilter = useCallback((key: keyof ClientsFiltersState) => {
    setFilters((prev) => ({ ...prev, [key]: "" }));
    setAppliedFilters((prev) => ({ ...prev, [key]: "" }));
    setPage(1);
  }, []);

  return {
    filters,
    setFilters,
    appliedFilters,
    page,
    pageSize,
    rows,
    total,
    isLoading,
    error,
    noAccess,
    hasActiveFilters,
    applyFilters,
    resetFilters,
    setPage,
    setPageSize: useCallback((size: number) => {
      setPageSize(size);
      setPage(1);
    }, []),
    refetch: fetchPage,
    removeFilter,
  };
}
